import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../firebase';
import { collection, query, where, onSnapshot, doc, updateDoc, writeBatch, deleteDoc, orderBy, limit, getDocs, getCountFromServer } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { IconButton, Badge, Menu, MenuItem, ListItemText, ListItemIcon, Divider, Typography, Box, Button, Tooltip } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import DeleteIcon from '@mui/icons-material/Delete';

// ============================================================
// BİLDİRİM SİSTEMİ AÇMA/KAPAMA ANAHTARI (arayüz tarafı)
// ============================================================
//
// 2026-07-20: Bildirimler kullanıcı talebiyle tamamen pasife alındı. Sunucu
// tarafındaki eşi: functions/services/notificationService.js -> BILDIRIMLER_AKTIF
//
// Kapalıyken zil hiç render edilmez ve Firestore'a hiçbir sorgu gitmez
// (ne listener ne de sayaç toplama sorgusu). Kod silinmedi; ileride kurallarla
// tekrar açılacak. İKİ dosyadaki anahtarı birden true yapmak gerekir.
const BILDIRIMLER_AKTIF = false;

// Zil menüsünde en fazla 10 bildirim gösteriliyor; listener'ı 30 ile sınırlamak
// hem menüyü besler hem de "biraz geçmiş" bırakır. Limit YOKKEN bu sorgu
// kullanıcının TÜM bildirimlerini (binlerce doküman) her uygulama açılışında
// okuyordu ve Firestore okuma faturasının en büyük kalemiydi.
const LISTENER_LIMIT = 30;

const NotificationBell = () => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);
    const prevUnreadCountRef = useRef(0);
    const isInitialMount = useRef(true);

    useEffect(() => {
        if (!BILDIRIMLER_AKTIF || !currentUser) return;
        const baseRef = collection(db, "notifications");
        const q = query(
            baseRef,
            where("userId", "==", currentUser.uid),
            orderBy("timestamp", "desc"),
            limit(LISTENER_LIMIT)
        );
        const unreadQuery = query(baseRef, where("userId", "==", currentUser.uid), where("isRead", "==", false));

        // Rozet sayısı, listener'daki 30 kayıttan türetilemez (30'dan fazla okunmamış
        // bildirim olabilir). getCountFromServer bir toplama sorgusudur: sonuç kaç
        // doküman olursa olsun okuma maliyeti ihmal edilebilir düzeydedir.
        const refreshUnreadCount = async () => {
            try {
                const snap = await getCountFromServer(unreadQuery);
                setUnreadCount(snap.data().count);
            } catch (error) {
                console.error("Okunmamış bildirim sayısı alınamadı:", error);
            }
        };

        const unsubscribe = onSnapshot(q, (snapshot) => {
            setNotifications(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            refreshUnreadCount();
        });
        return () => unsubscribe();
    }, [currentUser]);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            prevUnreadCountRef.current = unreadCount;
            return;
        }
        if (unreadCount > prevUnreadCountRef.current) {
            const audio = new Audio('/notification.mp3');
            audio.play().catch(error => { /* Kullanıcı etkileşimi olmadan ses çalınamadı, sessizce geç */ });
        }
        prevUnreadCountRef.current = unreadCount;
    }, [unreadCount]);


    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleNotificationClick = async (notification) => {
        if (!notification.isRead) {
            await updateDoc(doc(db, "notifications", notification.id), { isRead: true });
        }
        if (notification.link) {
            navigate(notification.link);
        }
        handleClose();
    };

    // Listener yalnızca son 30 bildirimi tuttuğu için toplu işlemler artık yerel
    // listeden değil, sunucudan çekilen tam kümeden çalışır. Bu sorgular seyrek
    // (kullanıcı butona bastığında) çalışır; sürekli açık bir listener değildir.
    const commitInChunks = async (docIds, apply) => {
        const CHUNK = 450; // writeBatch üst sınırı 500
        for (let i = 0; i < docIds.length; i += CHUNK) {
            const batch = writeBatch(db);
            docIds.slice(i, i + CHUNK).forEach(id => apply(batch, doc(db, "notifications", id)));
            await batch.commit();
        }
    };

    const handleMarkAllAsRead = async () => {
        if (!currentUser || unreadCount === 0) return;
        try {
            const snap = await getDocs(query(
                collection(db, "notifications"),
                where("userId", "==", currentUser.uid),
                where("isRead", "==", false)
            ));
            await commitInChunks(snap.docs.map(d => d.id), (batch, ref) => batch.update(ref, { isRead: true }));
        } catch (error) {
            console.error("Tüm bildirimler okunmuş olarak işaretlenirken hata: ", error);
        }
    };

    const handleDeleteNotification = async (e, notificationId) => {
        e.stopPropagation();
        try {
            await deleteDoc(doc(db, "notifications", notificationId));
        } catch (error) {
            console.error("Bildirim silinirken hata:", error);
        }
    };

    const handleDeleteAllRead = async () => {
        if (!currentUser) return;
        try {
            const snap = await getDocs(query(
                collection(db, "notifications"),
                where("userId", "==", currentUser.uid),
                where("isRead", "==", true)
            ));
            if (snap.empty) return;
            if (!window.confirm(`${snap.size} adet okunmuş bildirim silinecek. Devam etmek istiyor musunuz?`)) return;
            await commitInChunks(snap.docs.map(d => d.id), (batch, ref) => batch.delete(ref));
        } catch (error) {
            console.error("Okunmuş bildirimler silinirken hata:", error);
        }
    };

    // Tüm hook'lar yukarıda koşulsuz çağrıldı; render'ı burada kesmek React
    // hook kurallarını bozmaz.
    if (!BILDIRIMLER_AKTIF) return null;

    return (
        <>
            <IconButton color="inherit" onClick={handleOpen}>
                <Badge badgeContent={unreadCount} color="error"><NotificationsIcon /></Badge>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                MenuListProps={{ 'aria-labelledby': 'basic-button' }}
                sx={{ '& .MuiPaper-root': { width: 380, maxWidth: '100%' } }}
            >
                <Box sx={{ p: '8px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Bildirimler</Typography>
                </Box>
                <Divider />
                {notifications.length === 0 ? (
                    <MenuItem disabled><ListItemText primary="Yeni bildirim yok." /></MenuItem>
                ) : (
                    notifications.slice(0, 10).map(notif => (
                        <MenuItem
                            key={notif.id}
                            onClick={() => handleNotificationClick(notif)}
                            sx={{ backgroundColor: notif.isRead ? 'transparent' : 'action.hover', whiteSpace: 'normal', py: 1.5, display: 'flex', justifyContent: 'space-between' }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                <MarkEmailReadIcon fontSize="small" color={notif.isRead ? 'disabled' : 'primary'} />
                            </ListItemIcon>
                            <ListItemText primary={notif.title} secondary={notif.message} />
                            <Tooltip title="Bu bildirimi sil">
                                <IconButton size="small" onClick={(e) => handleDeleteNotification(e, notif.id)} sx={{ ml: 1 }}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </MenuItem>
                    ))
                )}
                {notifications.length > 0 && (
                    <>
                        <Divider />
                        <Box sx={{ p: 1, display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Button size="small" startIcon={<DoneAllIcon />} onClick={handleMarkAllAsRead} disabled={unreadCount === 0}>
                                Tümünü Okundu İşaretle
                            </Button>
                            <Button size="small" startIcon={<DeleteIcon />} color="error" onClick={handleDeleteAllRead} disabled={notifications.length === 0}>
                                Okunmuşları Sil
                            </Button>
                        </Box>
                    </>
                )}
            </Menu>
        </>
    );
};

export default NotificationBell;